import { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Typography,
  Tabs,
  Tab,
  TextField,
  Stack,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  AlertTitle,
  Alert
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { Feature } from 'models/api';
import useFetchSection from 'hooks/useFetchSection';
import { assignUser, getUser, getUsers } from 'framework/user';
import { formatDate } from 'helpers';
import TeethImagesTab from 'components/UI/TeethImages';
import { QueryClient, useMutation } from 'react-query';
import { IUser } from 'models/user';
import { useAlert } from 'contexts/alertContext';
import { handleError } from 'helpers/utils';
import AssignDoctorList from 'components/UI/AssignDoctorList';
import UserActivity from 'components/UI/Activity';
import Treatments from 'components/UI/Treatments';


interface Props {
  feature: Feature
}
const UserDetails: React.FC<Props> = ({ feature }) => {
  const params = useParams();

  const slugId = params[`${feature.singleName}Id`];

  const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const {
    loading,
    data: User,
    error,
    revalidate,
  } = useFetchSection({
    fetchData: () => getUser(slugId),
    fetchPermission: async () => null,
    section: "User Details"
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  if (error) return (
    <Alert severity="error">404 - Page not found</Alert>
  )

  return (
    <Box p={2}>
      <Typography
        variant="h3"
        color={"header.primary"}
        sx={{ mb: 4 }}
      >
        User Details
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4} lg={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 1 }} src={User?.profilePic} />
              <Typography variant="h6">{User?.firstName}</Typography>
              <Typography color="textSecondary">{User?.lastName}</Typography>
              <Box mt={2} textAlign="left">
                <Typography><strong>Email:</strong> {User?.email}</Typography>
                <Typography><strong>County Code:</strong>{User?.countryCode}</Typography>
                <Typography><strong>Contact Number:</strong> {User?.contactNumber}</Typography>
                <Typography><strong>Date of Birth:</strong> {formatDate(User?.dateOfBirth!)}</Typography>
                <Typography><strong>Status:</strong> Active</Typography>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 300, color: 'white', backgroundColor: '#007bff', p: '4px', px: 1 }}>
                About Me
              </Typography>
              <Typography><strong>City:</strong> {User?.cityName}</Typography>
              <Typography><strong>Country:</strong> {User?.countryName}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={8} lg={9}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2 }}
          >
            <Tab label="Assigned Doctor" />
            <Tab label="User Activity" />
            <Tab label="Treatments" />
          </Tabs>

          {/* Tab Panels */}
          {tabIndex === 0 && (
            <Box>
              <AssignDoctorList slugId={slugId!} />
            </Box>
          )}

          {/* {tabIndex === 1 && (
            <Box>
              <TeethImagesTab images={[User?.fontImage!, User?.upperImage!, User?.lowerImage!, User?.leftImage!, User?.rightImage!,]} />
            </Box>
          )} */}


          {tabIndex === 1 && (
            <Box>
              <UserActivity slugId={slugId!} />
            </Box>
          )}

          {tabIndex === 2 && (
            <Box>
              <Treatments slugId={slugId!} />
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserDetails;
